import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Question, QuestionType } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';

/** Config for a single field in the personal-details form. */
export interface PersonalDetailsFieldConfig {
  show: boolean;
  /** Defaults to true when the field is shown. */
  required?: boolean;
  /** Name/CPF as a string; DOB as 'YYYY-MM-DD'. */
  initialValue?: string;
}

/** Which of the three core fields to collect, and how. */
export interface PersonalDetailsFormConfig {
  legalName?: PersonalDetailsFieldConfig;
  dateOfBirth?: PersonalDetailsFieldConfig;
  cpf?: PersonalDetailsFieldConfig;
}

/** Emitted value — only the enabled fields are present. */
export interface PersonalDetailsValue {
  legalName?: string;
  dateOfBirth?: string; // 'YYYY-MM-DD'
  cpf?: string; // digits only (unmasked)
}

type FieldKey = 'legalName' | 'dateOfBirth' | 'cpf';

/**
 * Reusable, presentational form that collects the client's core identity
 * fields (legal name, date of birth, CPF). Which fields render is driven by
 * `config`. The CPF field uses the plain `app-question-cpf` (format validation
 * only) — there is deliberately NO leak/SERPRO/sigilo check here.
 *
 * The component makes no backend calls; it emits value + validity for a host
 * (e.g. a modal) to persist.
 */
@Component({
  selector: 'app-personal-details-form',
  templateUrl: './personal-details-form.component.html',
  styleUrls: ['./personal-details-form.component.scss'],
})
export class PersonalDetailsFormComponent implements OnInit {
  @Input() config: PersonalDetailsFormConfig = {};
  @Output() valueChange = new EventEmitter<PersonalDetailsValue>();
  @Output() validityChange = new EventEmitter<boolean>();

  legalName = '';
  dateOfBirth: Date | null = null;
  cpf = '';

  /** Plain-CPF question object driving <app-question-cpf> (no leak check). */
  cpfQuestion!: Question;

  /** Tracks the plain CPF component's own format validity. */
  private cpfFormatValid = false;

  ngOnInit(): void {
    this.legalName = this.config.legalName?.initialValue?.trim() ?? '';
    this.dateOfBirth = this.parseIsoDate(this.config.dateOfBirth?.initialValue);
    this.cpf = this.config.cpf?.initialValue ?? '';
    this.cpfQuestion = this.buildCpfQuestion();

    this.emit();
  }

  isShown(field: FieldKey): boolean {
    return this.config[field]?.show === true;
  }

  private isRequired(field: FieldKey): boolean {
    // Required by default when shown; opt out with required:false.
    return this.isShown(field) && this.config[field]?.required !== false;
  }

  get isValid(): boolean {
    if (this.isRequired('legalName') && !this.legalName.trim()) return false;
    if (this.isRequired('dateOfBirth') && !this.isValidDate(this.dateOfBirth)) return false;
    if (this.isRequired('cpf') && !this.cpfFormatValid) return false;
    return true;
  }

  onNameChange(value: string): void {
    this.legalName = value ?? '';
    this.emit();
  }

  onDateChange(value: Date | null): void {
    this.dateOfBirth = this.isValidDate(value) ? value : null;
    this.emit();
  }

  onCpfChange(answer: QuestionAnswer): void {
    this.cpf = (answer?.answer?.value ?? '').toString();
    this.cpfFormatValid = answer?.question?.isValid === true;
    this.emit();
  }

  private emit(): void {
    this.valueChange.emit(this.buildValue());
    this.validityChange.emit(this.isValid);
  }

  private buildValue(): PersonalDetailsValue {
    const value: PersonalDetailsValue = {};
    if (this.isShown('legalName')) {
      value.legalName = this.legalName.trim();
    }
    if (this.isShown('dateOfBirth')) {
      value.dateOfBirth = this.isValidDate(this.dateOfBirth)
        ? this.toIsoDate(this.dateOfBirth as Date)
        : undefined;
    }
    if (this.isShown('cpf')) {
      value.cpf = this.cpf.replace(/\D/g, '');
    }
    return value;
  }

  private buildCpfQuestion(): Question {
    const elective = !this.isRequired('cpf');
    return {
      id: 'personal-details-cpf',
      created_at: new Date(),
      name: 'CPF',
      slug: 'personal-details-cpf',
      statement: '',
      response_type: QuestionType.CPF,
      configuration: null,
      questionnaire_id: '',
      elective,
      answer: this.cpf ? ({ value: this.cpf } as any) : undefined,
      isValid: false,
    } as Question;
  }

  private isValidDate(value: Date | null): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseIsoDate(iso?: string): Date | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return this.isValidDate(date) ? date : null;
  }
}
